/**
 * Centralized analytics abstraction over `window.dataLayer` (Google Tag
 * Manager, container GTM-5T4N9G5R). GA4 (G-8CV6SCXFQJ) is configured inside
 * GTM as a Google Tag with Enhanced Measurement ON — so we do NOT push
 * manual page_view events. SPA route changes use pushState which Enhanced
 * Measurement handles natively.
 *
 * PRIVACY CONTRACT (spec section 5):
 *   • Only public entity IDs/slugs are accepted (school_id, article slug,
 *     ruang_id, discussion ID). Free-text keys are stripped by name.
 *   • Empty / falsy params are dropped so payloads stay clean.
 *   • Never throws — analytics failure NEVER breaks the app.
 */

// Keys that MUST NEVER appear in an analytics payload even if a caller passes
// them by accident. Any value shipped under these names is dropped.
const FORBIDDEN_KEYS = new Set([
  "email", "phone", "phone_number", "name", "full_name", "username",
  "password", "token", "access_token", "id_token", "authorization",
  "review_text", "review_body", "discussion_text", "discussion_body",
  "reply_text", "reply_body", "body", "message", "content",
  "search_text", "raw_query", "query_text", "query",
  "address", "user_id", "user_email",
]);

// De-dup ring for events that legitimately fire once per navigation but may
// be echoed by StrictMode / React remounts / effect re-runs. Keyed by
// `event + stable_id`. Cleared on route change via `trackPageChange()`.
const RECENT = new Map();
const RECENT_TTL_MS = 4000;

const _sanitize = (params) => {
  const out = {};
  if (!params || typeof params !== "object") return out;
  for (const [k, v] of Object.entries(params)) {
    if (FORBIDDEN_KEYS.has(k)) continue;
    if (v === null || v === undefined || v === "") continue;
    if (typeof v === "object") continue; // no nested user content
    out[k] = v;
  }
  return out;
};

// Fields that reliably identify a distinct entity/action. Presence of ANY of
// these turns on strict de-dup for that logical operation. Absence means
// the event has no reliable identity — in that case we DO NOT globally
// throttle by event name (spec section 2) and instead let it through.
const IDENTITY_KEYS = [
  "school_id", "school_slug", "article_slug", "article_id",
  "target_slug", "target_id", "discussion_id", "thread_id",
  "ruang_id", "ruang_slug", "slug",
];

const _identityFor = (params) => {
  for (const k of IDENTITY_KEYS) {
    if (params[k]) return String(params[k]);
  }
  return null;
};

const _shouldEmit = (event, params) => {
  const id = _identityFor(params);
  // No reliable identity → never suppress. A user submitting two different
  // searches, clicking two different related-article cards, or triggering
  // any event whose parameters can't discriminate one action from another
  // must all emit. Spec section 2 explicitly forbids event-name-only global
  // throttling.
  if (!id) return true;
  const key = `${event}:${id}`;
  const now = Date.now();
  const last = RECENT.get(key) || 0;
  if (now - last < RECENT_TTL_MS) return false;
  RECENT.set(key, now);
  if (RECENT.size > 200) {
    const cutoff = now - RECENT_TTL_MS;
    for (const [k, t] of RECENT) if (t < cutoff) RECENT.delete(k);
  }
  return true;
};

/**
 * Push a structured event to dataLayer. Silent no-op if dataLayer is absent
 * (SSR, ad-blocker, misconfig). Never throws.
 *
 * @param {string} event  GA4-style snake_case name (view_school, submit_review, …)
 * @param {object} [params]  Public IDs only. Free-text keys are stripped.
 */
export function track(event, params = {}) {
  try {
    if (typeof window === "undefined") return;
    if (!event || typeof event !== "string") return;
    const clean = _sanitize(params);
    if (!_shouldEmit(event, clean)) return;
    if (!window.dataLayer) window.dataLayer = [];
    window.dataLayer.push({ event, ...clean });
  } catch { /* analytics must never surface as a user-facing error */ }
}

/**
 * Reset the per-route de-dup cache on real navigation so the same event may
 * fire once per page. Call from a Router listener if you observe stale
 * suppression; usually not needed because ID-based keys already discriminate.
 */
export function trackPageChange() {
  RECENT.clear();
}

// Default export mirrors the conceptual `analytics.track(...)` interface.
export default { track, trackPageChange };
